import { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Space, Spin, Alert, message, Descriptions, Tag, Modal } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getTenantBrandings,
  getTenantBranding,
  createTenantBranding,
  updateTenantBranding,
  deleteTenantBranding,
  type TenantBranding,
  type CreateTenantBrandingRequest,
  type UpdateTenantBrandingRequest,
} from '@/api/adminApi';
import { useAuth } from '@/hooks/useAuth';
import AdminNav from './AdminNav';

type Mode = 'view' | 'edit' | 'create';

export default function BrandingPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();

  const canRead = hasPermission('admin', 'read');
  const canWrite = hasPermission('admin', 'write');
  const canDelete = hasPermission('admin', 'delete');

  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [mode, setMode] = useState<Mode>('view');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form] = Form.useForm();

  const loadBranding = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getTenantBrandings({ from: 0, size: 1 });
      if (result.items && result.items.length > 0) {
        const full = await getTenantBranding(result.items[0].id);
        setBranding(full);
        setMode('view');
      } else {
        setBranding(null);
        setMode('create');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('app.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  const handleEdit = () => {
    if (!branding) return;
    form.setFieldsValue({
      company_name: branding.company_name,
      logo_url: branding.logo_url || '',
      favicon_url: branding.favicon_url || '',
      primary_color: branding.primary_color || '#1890ff',
      secondary_color: branding.secondary_color || '#52c41a',
      accent_color: branding.accent_color || '#faad14',
      tagline: branding.tagline || '',
      footer_text: branding.footer_text || '',
      login_background_url: branding.login_background_url || '',
      custom_css: branding.custom_css || '',
    });
    setMode('edit');
  };

  const handleDelete = () => {
    if (!branding) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('admin.confirm_delete_branding'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteTenantBranding(branding.id);
          message.success(t('admin.branding_delete_success'));
          setBranding(null);
          setMode('create');
          form.resetFields();
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  };

  const handleSubmit = async (values: Record<string, string>) => {
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'edit' && branding) {
        const dto: UpdateTenantBrandingRequest = {
          company_name: values.company_name,
          logo_url: values.logo_url || undefined,
          favicon_url: values.favicon_url || undefined,
          primary_color: values.primary_color,
          secondary_color: values.secondary_color,
          accent_color: values.accent_color,
          tagline: values.tagline || null,
          footer_text: values.footer_text || null,
          login_background_url: values.login_background_url || null,
          custom_css: values.custom_css || null,
        };
        await updateTenantBranding(branding.id, dto);
        message.success(t('admin.branding_update_success'));
      } else {
        const dto: CreateTenantBrandingRequest = {
          company_name: values.company_name,
          logo_url: values.logo_url || undefined,
          favicon_url: values.favicon_url || undefined,
          primary_color: values.primary_color,
          secondary_color: values.secondary_color,
          accent_color: values.accent_color,
          tagline: values.tagline || undefined,
          footer_text: values.footer_text || undefined,
          login_background_url: values.login_background_url || undefined,
          custom_css: values.custom_css || undefined,
        };
        await createTenantBranding(dto);
        message.success(t('admin.branding_create_success'));
      }
      await loadBranding();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('app.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;
  if (loading) return <><AdminNav /><Spin size="large" /></>;

  const colorSwatch = (color: string) => (
    <Space>
      <div style={{ width: 20, height: 20, backgroundColor: color, border: '1px solid #d9d9d9', borderRadius: 4 }} />
      {color}
    </Space>
  );

  // View mode
  if (mode === 'view' && branding) {
    return (
      <div>
        <AdminNav />
        <Space style={{ marginBottom: 16 }}>
          {canWrite && (
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('app.edit')}
            </Button>
          )}
          {canDelete && (
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('app.delete')}
            </Button>
          )}
        </Space>

        <h2>{t('admin.branding_detail_title')}</h2>

        <Descriptions bordered column={2}>
          <Descriptions.Item label={t('admin.branding_field.company_name')}>{branding.company_name}</Descriptions.Item>
          <Descriptions.Item label={t('admin.branding_field.tagline')}>{branding.tagline || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('admin.branding_field.primary_color')}>{colorSwatch(branding.primary_color)}</Descriptions.Item>
          <Descriptions.Item label={t('admin.branding_field.secondary_color')}>{colorSwatch(branding.secondary_color)}</Descriptions.Item>
          <Descriptions.Item label={t('admin.branding_field.accent_color')}>{colorSwatch(branding.accent_color)}</Descriptions.Item>
          <Descriptions.Item label={t('admin.branding_field.logo_url')}>{branding.logo_url || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('admin.branding_field.favicon_url')} span={2}>{branding.favicon_url || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('admin.branding_field.footer_text')} span={2}>{branding.footer_text || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('admin.branding_field.login_background_url')} span={2}>{branding.login_background_url || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('admin.branding_field.custom_css')} span={2}>
            {branding.custom_css ? <pre style={{ margin: 0, maxHeight: 200, overflow: 'auto' }}>{branding.custom_css}</pre> : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('admin.field.created_at')}>
            {branding.created_at ? new Date(branding.created_at).toLocaleString() : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('admin.field.updated_at')}>
            {branding.updated_at ? new Date(branding.updated_at).toLocaleString() : '-'}
          </Descriptions.Item>
        </Descriptions>
      </div>
    );
  }

  // Create / Edit form
  return (
    <div>
      <AdminNav />
      <h2>{mode === 'edit' ? t('admin.edit_branding_title') : t('admin.create_branding_title')}</h2>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          primary_color: '#1890ff',
          secondary_color: '#52c41a',
          accent_color: '#faad14',
        }}
        style={{ maxWidth: 600 }}
      >
        <Form.Item
          name="company_name"
          label={t('admin.branding_field.company_name')}
          rules={[{ required: true, message: t('admin.validation.company_name_required') }]}
        >
          <Input placeholder={t('admin.branding_field.company_name')} />
        </Form.Item>

        <Form.Item name="tagline" label={t('admin.branding_field.tagline')}>
          <Input placeholder={t('admin.branding_field.tagline')} />
        </Form.Item>

        <Form.Item name="logo_url" label={t('admin.branding_field.logo_url')}>
          <Input placeholder="https://" />
        </Form.Item>

        <Form.Item name="favicon_url" label={t('admin.branding_field.favicon_url')}>
          <Input placeholder="https://" />
        </Form.Item>

        <Form.Item name="primary_color" label={t('admin.branding_field.primary_color')}>
          <Input type="color" style={{ width: 80, padding: 2 }} />
        </Form.Item>

        <Form.Item name="secondary_color" label={t('admin.branding_field.secondary_color')}>
          <Input type="color" style={{ width: 80, padding: 2 }} />
        </Form.Item>

        <Form.Item name="accent_color" label={t('admin.branding_field.accent_color')}>
          <Input type="color" style={{ width: 80, padding: 2 }} />
        </Form.Item>

        <Form.Item name="footer_text" label={t('admin.branding_field.footer_text')}>
          <Input.TextArea rows={2} placeholder={t('admin.branding_field.footer_text')} />
        </Form.Item>

        <Form.Item name="login_background_url" label={t('admin.branding_field.login_background_url')}>
          <Input placeholder="https://" />
        </Form.Item>

        <Form.Item name="custom_css" label={t('admin.branding_field.custom_css')}>
          <Input.TextArea rows={4} placeholder=".my-class { color: red; }" />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            {mode === 'edit' && (
              <Button onClick={() => setMode('view')}>{t('app.cancel')}</Button>
            )}
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
